/**
 * "Propose as a new duty" — V112-5-5 (ADR-0116, owner D-BL55-2..4).
 *
 * Shown in a duty card when its short name is not a known duty. The editor names the duty for reviewers, says what it
 * requires and picks the control that checks it (an existing one, or a new one). Nothing is filed here: the new duty
 * travels with the rule, and filing the rule files the control, the duty and the rule together for one review.
 */
import { Button, Field, Input, Radio, Select, Textarea } from '@aigovops/design-system';
import { newDutyProblems, type NewDutyDraft } from '../lib/rule-authoring';

export interface NewDutyFieldsProps {
  /** The duty card's key, for field ids. */
  dutyKey: number;
  /** "Duty 2", for the close button's accessible name. */
  dutyLabel: string;
  /** The duty card's title: the placeholder for the new control's title. */
  titleHint: string;
  value: NewDutyDraft;
  controls: ReadonlyArray<{ control_id: string; title: string }>;
  /** After a refused submit: show each field's problem next to it. */
  showErrors: boolean;
  onChange: (patch: Partial<NewDutyDraft>) => void;
  onClose: () => void;
}

export function NewDutyFields({ dutyKey: k, dutyLabel, titleHint, value, controls, showErrors, onChange, onClose }: NewDutyFieldsProps) {
  const errors = showErrors ? Object.fromEntries(newDutyProblems(value).map((p) => [p.field, p.text.replace(/^./, (c) => c.toUpperCase())])) : {};
  return (
    <fieldset
      style={{ margin: 0, display: 'grid', gap: 'var(--space-2)', padding: 'var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-elevated)' }}
    >
      <legend style={{ padding: 0, fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>New duty</legend>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="button" compact variant="ghost" onClick={onClose} aria-label={`Don’t propose a new duty, ${dutyLabel}`}>
          Don’t propose
        </Button>
      </div>
      <Field id={`ndl-${k}`} label="Name reviewers see" required error={errors.ndl} errorRole="none">
        <Input value={value.label} onChange={(e) => onChange({ label: e.target.value })} />
      </Field>
      <Field id={`ndd-${k}`} label="What it requires" hint="One or two sentences, in your own words." required error={errors.ndd} errorRole="none">
        <Textarea rows={2} value={value.definition} onChange={(e) => onChange({ definition: e.target.value })} />
      </Field>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Checked by</legend>
        {controls.length ? (
          <Radio id={`ndm-existing-${k}`} name={`ndm-${k}`} label="A control we have" checked={value.controlMode === 'existing'} onChange={() => onChange({ controlMode: 'existing' })} />
        ) : null}
        <Radio id={`ndm-new-${k}`} name={`ndm-${k}`} label="A new control" checked={value.controlMode === 'new'} onChange={() => onChange({ controlMode: 'new' })} />
      </fieldset>
      {value.controlMode === 'existing' && controls.length ? (
        <Field id={`ndc-${k}`} label="Control" required error={errors.ndc} errorRole="none">
          <Select value={value.controlId} onChange={(e) => onChange({ controlId: e.target.value })}>
            <option value="">Choose a control</option>
            {controls.map((c) => (
              <option key={c.control_id} value={c.control_id}>
                {c.title}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <>
          <Field id={`ndt-${k}`} label="Control title" required error={errors.ndt} errorRole="none">
            <Input value={value.controlTitle} placeholder={titleHint ? `e.g. ${titleHint}` : undefined} onChange={(e) => onChange({ controlTitle: e.target.value })} />
          </Field>
          <Field id={`ndx-${k}`} label="What the control checks" required error={errors.ndx} errorRole="none">
            <Textarea rows={2} value={value.controlDescription} onChange={(e) => onChange({ controlDescription: e.target.value })} />
          </Field>
        </>
      )}
    </fieldset>
  );
}
